import { createContext, useContext } from 'react';

export interface WindowContentSize {
  /** Content area width in px (inside title bar / borders). Updates as the OS window is resized. */
  width: number;
  /** Content area height in px. */
  height: number;
}

const defaultSize: WindowContentSize = { width: 0, height: 0 };

export const WindowContentSizeContext = createContext<WindowContentSize>(defaultSize);

/** Read the parent WindowFrame content box size (container-based, not viewport). */
export function useWindowContentSize(): WindowContentSize {
  return useContext(WindowContentSizeContext);
}

/**
 * Named breakpoints against the OS window content width.
 * Prefer these over Chakra `base`/`md` props inside desktop windows.
 */
export function useWindowBreakpoints() {
  const { width, height } = useWindowContentSize();
  return {
    width,
    height,
    /** Very narrow window (stacked / mobile-like). */
    isXs: width > 0 && width < 480,
    /** Card-style lists, tighter padding. */
    isSm: width > 0 && width < 680,
    /** Mid window. */
    isMd: width >= 680 && width < 960,
    /** Wide window. */
    isLg: width >= 960,
    isReady: width > 0,
  };
}

export default WindowContentSizeContext;
